import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useNodesState,
  useEdgesState,
  MarkerType,
} from '@xyflow/react';
import { DEFAULT_TEMPLATE, generateId } from '../caseFileConstants';

const useProgressTemplate = (threadId, authenticatedAxios, loadProgress, setPipelineName) => {
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [editMode, setEditMode] = useState(false);
  const [modal, setModal] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Load progress and set template
  const refreshProgress = useCallback(async () => {
    const data = await loadProgress();
    if (data?.template) {
      setTemplate(data.template);
    }
  }, [loadProgress]);

  // Listen for pipeline assignment events (when program is changed)
  useEffect(() => {
    const handlePipelineAssigned = (event) => {
      const { pipelineName: newPipelineName } = event.detail || {};
      console.log('[CaseFile] Pipeline assigned event received:', newPipelineName);
      
      // Update the pipeline name
      if (newPipelineName) {
        setPipelineName(newPipelineName);
      }
      
      // Reload progress to get the new pipeline template
      refreshProgress();
    };

    window.addEventListener('pipelineAssigned', handlePipelineAssigned);
    return () => {
      window.removeEventListener('pipelineAssigned', handlePipelineAssigned);
    };
  }, [refreshProgress, setPipelineName]);

  // Also load on mount
  useEffect(() => {
    refreshProgress();
  }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // PROGRESS LOGIC
  // ============================================

  const saveProgress = useCallback(async (newTemplate) => {
    if (!threadId) return;
    try {
      await authenticatedAxios.post(`/case-progress/${threadId}`, { template: newTemplate });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [threadId, authenticatedAxios]);

  const progressPercentage = useMemo(() => {
    let total = 0;
    let completed = 0;
    template.milestones.forEach(milestone => {
      milestone.steps.forEach(step => {
        total++;
        if (step.status === 'completed') completed++;
      });
    });
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }, [template]);

  const getMilestoneStatus = useCallback((milestone) => {
    const steps = milestone.steps || [];
    if (steps.length === 0) return 'pending';
    
    const allCompleted = steps.every(s => s.status === 'completed');
    const allAutoDetected = steps.every(s => s.status === 'auto_detected' || s.status === 'completed');
    const anyAutoDetected = steps.some(s => s.status === 'auto_detected');
    const anyInProgress = steps.some(s => s.status === 'in_progress');
    const anyCompleted = steps.some(s => s.status === 'completed');
    
    if (allCompleted) return 'completed';
    if (allAutoDetected && anyAutoDetected) return 'auto_detected';
    if (anyInProgress || anyCompleted || anyAutoDetected) return 'in_progress';
    return 'pending';
  }, []);

  const cycleStepStatus = useCallback((milestoneId, stepId) => {
    const newTemplate = {
      ...template,
      milestones: template.milestones.map(m => {
        if (m.id === milestoneId) {
          return {
            ...m,
            steps: m.steps.map(s => {
              if (s.id === stepId) {
                let nextStatus;
                if (s.status === 'auto_detected') {
                  nextStatus = 'completed';
                } else if (s.status === 'pending') {
                  nextStatus = 'in_progress';
                } else if (s.status === 'in_progress') {
                  nextStatus = 'completed';
                } else {
                  nextStatus = 'pending';
                }
                return { 
                  ...s, 
                  status: nextStatus, 
                  autoDetected: false,
                  completedAt: nextStatus === 'completed' ? new Date().toISOString() : s.completedAt
                };
              }
              return s;
            })
          };
        }
        return m;
      })
    };
    setTemplate(newTemplate);
    saveProgress(newTemplate);
  }, [template, saveProgress]);

  const addStep = useCallback((milestoneId) => {
    setModal({ type: 'step', milestoneId });
    setInputValue('');
  }, []);

  const saveStep = useCallback(() => {
    if (!inputValue.trim() || !modal?.milestoneId) return;
    
    const newStep = {
      id: generateId(),
      text: inputValue.trim(),
      status: 'pending'
    };
    
    const newTemplate = {
      ...template,
      milestones: template.milestones.map(m => {
        if (m.id === modal.milestoneId) {
          return { ...m, steps: [...m.steps, newStep] };
        }
        return m;
      })
    };
    
    setTemplate(newTemplate);
    saveProgress(newTemplate);
    setModal(null);
  }, [inputValue, modal, template, saveProgress]);

  const addMilestone = useCallback(() => {
    setModal({ type: 'milestone' });
    setInputValue('');
  }, []);

  const saveMilestone = useCallback(() => {
    if (!inputValue.trim()) return;
    
    const newMilestone = {
      id: generateId(),
      title: inputValue.trim(),
      order: template.milestones.length + 1,
      steps: []
    };
    
    const newTemplate = {
      ...template,
      milestones: [...template.milestones, newMilestone]
    };
    
    setTemplate(newTemplate);
    saveProgress(newTemplate);
    setModal(null);
  }, [inputValue, template, saveProgress]);

  const deleteStep = useCallback((milestoneId, stepId) => {
    const newTemplate = {
      ...template,
      milestones: template.milestones.map(m => {
        if (m.id === milestoneId) {
          return { ...m, steps: m.steps.filter(s => s.id !== stepId) };
        }
        return m;
      })
    };
    setTemplate(newTemplate);
    saveProgress(newTemplate);
  }, [template, saveProgress]);

  const deleteMilestone = useCallback((milestoneId) => {
    const newTemplate = {
      ...template,
      milestones: template.milestones
        .filter(m => m.id !== milestoneId)
        .map((m, idx) => ({ ...m, order: idx + 1 }))
    };
    setTemplate(newTemplate);
    saveProgress(newTemplate);
  }, [template, saveProgress]);

  const onNodeDragStop = useCallback((event, node) => {
    if (!editMode || node.type !== 'milestone') return;
    
    const newTemplate = {
      ...template,
      milestones: template.milestones.map(m => {
        if (m.id === node.id) {
          return { ...m, position: { x: node.position.x, y: node.position.y } };
        }
        return m;
      })
    };
    setTemplate(newTemplate);
    saveProgress(newTemplate);
  }, [editMode, template, saveProgress]);

  // Generate nodes and edges
  useEffect(() => {
    const HORIZONTAL_GAP = 380; // Increased from 280 for more spacing between pipeline items
    const START_X = 50;
    const Y_CENTER = 200;
    
    const newNodes = [];
    const newEdges = [];
    
    newNodes.push({
      id: 'start',
      type: 'startEnd',
      position: { x: START_X, y: Y_CENTER },
      data: { label: 'START', isEnd: false },
      draggable: editMode
    });
    
    template.milestones.forEach((milestone, index) => {
      const status = getMilestoneStatus(milestone);
      const defaultX = START_X + (index + 1) * HORIZONTAL_GAP;
      const defaultY = Y_CENTER - 60;
      // Always use calculated X position for equal spacing
      // Only use saved Y position if in edit mode (allows vertical adjustment only)
      const position = editMode && milestone.position 
        ? { x: defaultX, y: milestone.position.y || defaultY }
        : { x: defaultX, y: defaultY };
      
      newNodes.push({
        id: milestone.id,
        type: 'milestone',
        position: position,
        data: {
          ...milestone,
          milestoneId: milestone.id,
          status,
          order: index + 1,
          editMode,
          onStepClick: cycleStepStatus,
          onAddStep: addStep,
          onDeleteStep: deleteStep,
          onDeleteMilestone: deleteMilestone
        },
        draggable: editMode
      });
      
      const prevId = index === 0 ? 'start' : template.milestones[index - 1].id;
      const prevStatus = index === 0 ? 'completed' : getMilestoneStatus(template.milestones[index - 1]);
      const edgeColor = prevStatus === 'completed' ? '#10b981' : 
                        prevStatus === 'auto_detected' ? '#ec4899' : 'rgba(75, 85, 99, 0.5)';
      
      newEdges.push({
        id: `e-${prevId}-${milestone.id}`,
        source: prevId,
        target: milestone.id,
        type: 'smoothstep',
        style: { stroke: edgeColor, strokeWidth: 3 },
        markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor }
      });
    });
    
    const endX = START_X + (template.milestones.length + 1) * HORIZONTAL_GAP;
    const lastId = template.milestones.length > 0 ? template.milestones[template.milestones.length - 1].id : 'start';
    const lastMilestoneStatus = template.milestones.length > 0 ? getMilestoneStatus(template.milestones[template.milestones.length - 1]) : 'pending';
    
    if (editMode) {
      newNodes.push({
        id: 'add-milestone',
        type: 'addMilestone',
        position: { x: endX, y: Y_CENTER - 20 },
        data: { onClick: addMilestone },
        draggable: false
      });
      
      newEdges.push({
        id: `e-${lastId}-add`,
        source: lastId,
        target: 'add-milestone',
        type: 'smoothstep',
        style: { stroke: 'rgba(99, 102, 241, 0.3)', strokeWidth: 2, strokeDasharray: '5,5' }
      });
      
      newNodes.push({
        id: 'finish',
        type: 'startEnd',
        position: { x: endX + 120, y: Y_CENTER },
        data: { label: 'FINISH', isEnd: true },
        draggable: true
      });
      
      newEdges.push({
        id: `e-add-finish`,
        source: 'add-milestone',
        target: 'finish',
        type: 'smoothstep',
        style: { stroke: 'rgba(75, 85, 99, 0.3)', strokeWidth: 2, strokeDasharray: '5,5' }
      });
    } else {
      newNodes.push({
        id: 'finish',
        type: 'startEnd',
        position: { x: endX, y: Y_CENTER },
        data: { label: 'FINISH', isEnd: true },
        draggable: false
      });
      
      const finishEdgeColor = lastMilestoneStatus === 'completed' ? '#10b981' : 
                              lastMilestoneStatus === 'auto_detected' ? '#ec4899' : 'rgba(75, 85, 99, 0.5)';
      
      newEdges.push({
        id: `e-${lastId}-finish`,
        source: lastId,
        target: 'finish',
        type: 'smoothstep',
        style: { stroke: finishEdgeColor, strokeWidth: 3 },
        markerEnd: { type: MarkerType.ArrowClosed, color: finishEdgeColor }
      });
    }
    
    setNodes(newNodes);
    setEdges(newEdges);
  }, [template, editMode, getMilestoneStatus, cycleStepStatus, addStep, addMilestone, deleteStep, deleteMilestone, setNodes, setEdges]);

  return {
    template,
    editMode, setEditMode,
    modal, setModal,
    inputValue, setInputValue,
    nodes, edges, onNodesChange, onEdgesChange,
    progressPercentage,
    getMilestoneStatus,
    cycleStepStatus,
    addStep,
    saveStep,
    addMilestone,
    saveMilestone,
    deleteStep,
    deleteMilestone,
    onNodeDragStop,
    refreshProgress,
  };
};

export default useProgressTemplate;
